import {
    getPaymentIntent,
    listPaymentIntents
} from './queries';
import {
    createPaymentIntent,
    deletePaymentIntent,
    updatePaymentIntent,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    CreatePaymentIntent,
    DeletePaymentIntent,
    GetPaymentIntent,
    ListPaymentIntents,
    PaymentIntent,
    UpdatePaymentIntent
} from "../types.ts";
import {generateClient} from "aws-amplify/api";


const client = generateClient();

export const fetchRecords = async (filter: object, limit?: number, nextToken?: string) => {

    const response = await client.graphql<GraphQLQuery<ListPaymentIntents>>({
                                                                                query: listPaymentIntents,
                                                                                variables: {filter: filter, limit, nextToken},
                                                                            });
    return response.data?.listPaymentIntents;
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetPaymentIntent>>({
                                                                              query: getPaymentIntent,
                                                                              variables: {id: id},
                                                                          });
    return response.data?.getPaymentIntent;
};

/**
 * Copies only the declared fields off `record`, leaving the caller's object
 * untouched. A field that is absent stays absent rather than being sent as
 * `undefined`.
 *
 * ⚠️ The copy is the point (FNP-996): building the payload by COPYING declared
 * fields out can never mutate the source, whereas the `delete payload.x` shape
 * it replaces is one missing spread away from editing the caller's live Formik
 * object.
 */
const pickDeclared = (
    record: Record<string, unknown>,
    fields: readonly string[],
): Record<string, unknown> =>
    fields.reduce<Record<string, unknown>>((input, field) => {
        if (typeof record[field] !== 'undefined') {
            input[field] = record[field];
        }
        return input;
    }, {});

/**
 * Fields `CreatePaymentIntentInput` declares. `amount` and `currency` are the
 * only required ones.
 *
 * ⚠️ `id`, `status`, `created` and `clientSecret` are NOT here and never were —
 * they are assigned by Stripe. `automaticPaymentMethods` IS declared (as
 * `AutomaticPaymentMethodsInput`), unlike on the update input.
 */
const CREATE_INPUT_FIELDS = [
    'amount',
    'currency',
    'automaticPaymentMethods',
    'captureMethod',
    'confirmationMethod',
    'customerId',
    'description',
    'paymentMethod',
    'paymentMethodTypes',
    'receiptEmail',
    'setupFutureUsage',
    'statementDescriptor',
    'statementDescriptorSuffix',
] as const;

/**
 * ⚠️ MONEY PATH — this CREATES a payment intent.
 *
 * The call itself is unchanged. What changed is that the payload is now built
 * by ALLOWLIST rather than passed through whole: the caller previously handed
 * in a `{...values}` object with `delete payload.created` applied, which left
 * `id: ''` and `status` in the input. Neither is declared on
 * `CreatePaymentIntentInput`, so AppSync dropped them silently.
 *
 * No idempotency key handling is introduced or altered here.
 */
export const add = async (payload: object) => {

    const input = pickDeclared(
        payload as Record<string, unknown>,
        CREATE_INPUT_FIELDS,
    );

    const response = await client.graphql<GraphQLQuery<CreatePaymentIntent>>({
                                                                                 query: createPaymentIntent,
                                                                                 variables: {input},
                                                                             });

    return response.data?.createPaymentIntent;
};

/**
 * Fields `UpdatePaymentIntentInput` actually declares, minus `id` which is
 * supplied separately below.
 *
 * ═══ AN ALLOWLIST, NOT A DENYLIST (FNP-996) ═══
 *
 * This was `{...record}` with `delete payload.created` — a DENYLIST. Two
 * failure modes, both live:
 *
 *   1. It only removed what someone remembered to name. The record handed in
 *      carries `status`, `clientSecret`, `automaticPaymentMethods` and
 *      `createdAtMillis` (the client-derived field added in FNP-918), none of
 *      which `UpdatePaymentIntentInput` declares. AppSync silently DROPS
 *      undeclared input fields, so the mutation appeared to succeed while
 *      quietly discarding part of what was sent — and any field added to the
 *      type later would ride along unnoticed.
 *   2. `delete` on a denylist is one edit away from mutating the CALLER's
 *      object. `PricesService.update` and `ProductsService.update` ran
 *      `delete record.created` on the live Formik object, which is FNP-996.
 *      The copy below happens first and the source is never touched.
 *
 * Verified against
 * BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentIntents.graphql.
 *
 * ⚠️ MONEY PATH: this NARROWS what is transmitted to exactly what the schema
 * accepts. It does not change confirm/capture/cancel semantics, any idempotency
 * key, or `clientSecret` handling — `clientSecret` is not an input field at all
 * and was never legitimately part of this payload.
 */
const UPDATE_INPUT_FIELDS = [
    'amount',
    'currency',
    'customerId',
    'description',
    'paymentMethod',
    'receiptEmail',
    'setupFutureUsage',
    'statementDescriptor',
    'statementDescriptorSuffix',
] as const;

export const update = async (id: string, record: PaymentIntent) => {

    // ⚠️ `id` comes from the ARGUMENT, not from the record. The old signature
    // took `_id` and ignored it, relying on `record.id` — so an edit form whose
    // record had been reshaped would have updated whichever id the object
    // happened to carry.
    const payload = {
        id,
        ...pickDeclared(record as unknown as Record<string, unknown>, UPDATE_INPUT_FIELDS),
    };

    const response = await client.graphql<GraphQLQuery<UpdatePaymentIntent>>({
                                                                                 query: (updatePaymentIntent),
                                                                                 variables: {input: payload},
                                                                             });
    return response.data?.updatePaymentIntent;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeletePaymentIntent>>({
                                                                                 query: (deletePaymentIntent),
                                                                                 variables: {input: {id: id}},

                                                                             });
    return response.data?.deletePaymentIntent;
};
